/**
 * SOU (Single Offer Unit) Styles
 *
 * This module contains all CSS styles for the SOU components, organized for:
 * 1. Static inline styles (JS objects) - used directly in component style props
 * 2. Dynamic stylesheet (string) - for media queries, animations, pseudo-classes
 *
 * This replaces the SOU.css file for better bundling and Shadow DOM injection.
 */
// CSS Variables (used in animations)
export const CSS_VARS = {
    shineDegree: '120deg',
    shineColor: 'rgba(255, 255, 255, 0.384)',
    shineTransition: 'all 0.65s ease-in-out',
};
// ============================================================================
// STATIC INLINE STYLES (can be spread into style={{}})
// ============================================================================
export const popupStyles = {
    minWidth: '90%',
    height: 'max-content',
    position: 'fixed',
    zIndex: 2147483647,
    lineHeight: 'normal',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    perspective: '1000px',
    transformStyle: 'preserve-3d',
    backfaceVisibility: 'visible',
};
export const modalHeaderStyles = {
    backgroundColor: 'rgb(11, 25, 55)',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
};
export const modalHeaderTextStyles = {
    maxWidth: '18rem',
    display: '-webkit-box',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
};
export const leadInStyles = {
    marginTop: 0,
    marginBottom: '12px',
    fontSize: '14px',
    display: 'flex',
    width: '100%',
    padding: '0 24px',
    boxSizing: 'border-box',
    textAlign: 'start',
    fontWeight: 400,
};
export const bodyStyles = {
    marginTop: 0,
    display: '-webkit-box',
    WebkitLineClamp: 6,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    backfaceVisibility: 'hidden',
};
export const headingStyles = {
    width: '100%',
    fontWeight: 'bold',
    marginTop: '18px',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    backfaceVisibility: 'hidden',
};
export const closeBtnStyles = {
    cursor: 'pointer',
    position: 'absolute',
    fontSize: '24px',
    right: '10px',
    top: '8px',
};
export const progressbarWrapperStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '10px 0',
    padding: '0 24px',
};
export const progressbarContainerStyles = {
    display: 'flex',
};
export const stepperContainerStyles = {
    display: 'flex',
    justifyContent: 'center',
};
export const stepCounterStyles = {
    position: 'relative',
    zIndex: 5,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    background: 'rgb(211, 211, 211)',
    fontSize: '16px',
};
export const numberStyles = {
    borderRadius: '50%',
    border: '1px solid black',
    padding: '6px',
    marginLeft: '8px',
};
export const modalContentContainerStyles = {
    padding: '0 24px',
    textAlign: 'center',
    marginTop: '12px',
    lineHeight: 'normal',
    minHeight: '116px',
    maxHeight: '500px',
    height: 'max-content',
};
export const imgContainerStyles = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '18px',
    marginLeft: '15px',
};
export const logoStyles = {
    maxWidth: '120px',
    height: '120px',
    objectFit: 'contain',
    cursor: 'pointer',
    borderRadius: '12px',
};
export const textContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
};
export const ctaContainerStyles = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '12px',
};
export const ctaButtonBaseStyles = {
    padding: '8px 12px',
    display: 'flex',
    fontSize: '16px',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: '24px',
    cursor: 'pointer',
    borderRadius: '0.25em',
    textDecoration: 'none',
};
export const rightCtaStyles = {
    ...ctaButtonBaseStyles,
    marginLeft: '12px',
};
export const footerTextContainerStyles = {
    width: '100%',
    textAlign: 'center',
    borderTop: '#ddd 1px solid',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
};
export const footerTextStyles = {
    padding: '12px',
};
export const modalFooterStyles = {
    paddingRight: '24px',
    fontSize: '10px',
};
export const footerLinkStyles = {
    textDecoration: 'none',
    color: '#777',
};
export const footerDisclaimerStyles = {
    fontSize: '10px',
    textAlign: 'center',
    marginBottom: '2px',
    color: '#777',
};
export const carouselContainerStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    alignItems: 'center',
    overflow: 'hidden',
    width: '155px',
    transition: 'transform 0.5s ease-in-out',
};
export const carouselImgStyles = {
    maxWidth: '45px',
    height: '45px',
    objectFit: 'contain',
};
export const offerwallCtaStyles = {
    marginBottom: '2px',
    height: 'max-content',
};
export const offerwallCtaContainerStyles = {
    display: 'flex',
    justifyContent: 'flex-end',
};
export const offerwallIframeStyles = {
    width: '100%',
    height: '80vh',
};
export const tcButtonStyles = {
    fontSize: '12px',
    cursor: 'pointer',
    color: '#777',
    marginLeft: '8px',
    position: 'absolute',
    bottom: '26px',
    right: '25px',
};
export const perksWalletStyles = {
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    color: 'black',
    textDecoration: 'underline',
};
export const positiveCtaContainerStyles = {
    flexGrow: 0.55,
};
export const negativeCtaContainerStyles = {
    flexGrow: 0.25,
};
export const pixelTrackerStyles = {
    display: 'none',
    width: '1px',
    height: '1px',
};
// Shimmer button styles
export const btnShimmerStyles = {
    position: 'relative',
    overflow: 'hidden',
    padding: '1rem 3rem',
    textDecoration: 'none',
};
// ============================================================================
// DYNAMIC STYLESHEET (for media queries, animations, pseudo-classes)
// ============================================================================
/**
 * Returns the CSS stylesheet string for injection into Shadow DOM.
 * Contains media queries, keyframe animations, and pseudo-class rules
 * that cannot be expressed as inline styles.
 */
export function getSOUStylesheet() {
    return `
/* Shine / shimmer tokens — :host so shadow tree always resolves var(); :root matches legacy */
:host,
:root {
  --shine-degree: ${CSS_VARS.shineDegree};
  --shine-color: ${CSS_VARS.shineColor};
  --shine-effect: linear-gradient(var(--shine-degree), transparent, var(--shine-color), transparent);
  --shine-transition: ${CSS_VARS.shineTransition};
}

/* Mobile Responsive - max-width: 690px */
@media (max-width: 690px) {
  .adpx_progressbar-wrapper {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 8px 0;
    gap: 6px;
  }
  #adpx_tc-button {
    bottom: 50px;
  }
  #adpx_popup,
  #adpx_right-cta,
  #adpx_left-cta,
  #adpx_offerwall-cta {
    cursor: default;
  }
  #adpx_left-cta,
  #adpx_offerwall-cta,
  #adpx_offerwall-cta-mobile,
  #adpx_right-cta,
  .step-counter {
    font-size: 14px;
  }
  #adpx_offerwall-cta-container {
    margin: 0 !important;
  }
  #adpx_offerwall_iframe {
    height: 60vh;
  }
  .adpx_progressbar-container {
    display: flex;
    justify-content: space-evenly;
    width: 100%;
    gap: 12px;
  }
  .adpx_img-container {
    display: none !important;
  }
  #adpx_Logo-mobile {
    display: block !important;
    margin: 12px 0;
  }
  #adpx_lead-in {
    text-align: center;
    margin-left: 0;
  }
}

/* Small Mobile - max-width: 438px */
@media (max-width: 438px) {
  .adpx_modal-content-container {
    max-height: 600px !important;
  }
  #adpx_Body {
    -webkit-line-clamp: 4 !important;
  }
  .adpx_carousel-img {
    max-width: 30px !important;
    height: 30px !important;
    object-fit: contain;
  }
  #adpx_close-btn {
    top: 6px;
  }
  #adpx_carousel-container-mobile {
    width: 105px !important;
  }
  #adpx_footer-disclaimer,
  #adpx_modal-footer {
    font-size: 8px !important;
  }
  #adpx_modal-footer {
    padding-right: 0px;
  }
  #adpx_Heading {
    margin-top: 12px !important;
  }
  #adpx_prev-carousel-svg-mobile,
  #adpx_next-carousel-svg-mobile {
    height: 12px;
  }
}

/* Desktop - min-width: 690px */
@media (min-width: 690px) {
  #adpx_popup {
    min-width: 600px;
  }
  .adpx_modal-content-container {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: flex-start;
    text-align: left;
  }
  #adpx_modal-header-text {
    max-width: 18rem !important;
    -webkit-line-clamp: 2;
  }
  #adpx_Heading,
  #adpx_lead-in {
    -webkit-line-clamp: 4;
  }
  #adpx_Logo,
  #adpx_Logo-mobile {
    object-fit: contain;
    border-radius: 12px;
  }
  #adpx_Body {
    margin-top: 12px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .adpx_cta-container {
    justify-content: flex-start;
  }
  #adpx_footer-text-container {
    text-align: right;
  }
  .adpx_carousel-container-mobile {
    display: none !important;
  }
  #adpx_img_container {
    margin: 18px 0px 0 12px;
    align-self: center;
  }
}

/* Tablet - max-width: 768px */
@media (max-width: 768px) {
  .adpx_modal-content-container {
    margin-top: 0;
  }
  #adpx_popup {
    position: fixed;
    transform: translate(-50%, -50%);
    top: 50%;
    left: 50%;
  }
}

/* Tablet - min-width: 768px */
@media (min-width: 768px) {
  #adpx_footer-text-container {
    border-top: #ddd 1px solid;
    display: flex;
    flex-direction: row;
  }
  #adpx_footer-disclaimer {
    font-size: 10px;
    text-align: left;
  }
  .adpx_text-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
}

/* Large Desktop - min-width: 1024px */
@media (min-width: 1024px) {
  #adpx_popup {
    min-width: 700px;
    max-width: 700px;
  }
  #adpx_footer-text-container {
    flex-direction: column;
    text-align: right;
    align-items: stretch;
  }
  .adpx_modal-content-container {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: flex-start;
    text-align: left;
  }
  #adpx_Logo,
  #adpx_Logo-mobile {
    object-fit: contain;
    border-radius: 12px;
  }
  #adpx_modal-header-text {
    max-width: 38rem !important;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  #adpx_Body {
    margin-top: 12px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .adpx_cta-container {
    justify-content: flex-start;
  }
  .adpx_right-container.adpx_no-img-right {
    max-width: 38rem;
  }
  .adpx_text-container {
    align-items: flex-start;
  }
}

/* Extra small - max-width: 360px */
@media (max-width: 360px) {
  .adpx_text-container {
    margin-top: 0px !important;
  }
  #adpx_carousel-container,
  #adpx_carousel-container-mobile {
    width: 140px;
  }
  #adpx_offerwall-cta,
  #adpx_offerwall-cta-mobile {
    font-size: 12px !important;
    -webkit-line-clamp: 2;
  }
}

/* Short viewport - max-height: 660px */
@media (max-height: 660px) {
  #adpx_lead-in,
  #adpx_Heading,
  #adpx_Body,
  #adpx_product-description > p,
  #adpx_product-description > h1,
  #adpx_product-description > h2,
  #adpx_product-description > h3,
  #adpx_product-description > h4,
  #adpx_product-description > h5,
  #adpx_product-description > span,
  #adpx_Body > p {
    font-size: 12px !important;
  }
  #adpx_footer-text,
  #adpx_footer-privacy,
  #adpx_footer-pub-privacy {
    font-size: 8px !important;
  }
}

/* Landscape mobile */
@media only screen and (max-device-width: 670px) and (orientation: landscape) {
  #adpx_modal-header-text {
    max-width: 38rem !important;
  }
  #adpx_popup {
    min-height: auto;
  }
}

@media only screen and (min-device-width: 480px) and (max-device-width: 690px) and (orientation: landscape) {
  #adpx_popup {
    height: 95%;
  }
  .adpx_modal-content-container {
    height: 45% !important;
    max-height: 50% !important;
    min-height: 36%;
    overflow-x: hidden;
    overflow-y: auto;
  }
}

/* Small mobile container */
@media only screen and (min-width: 280px) and (max-width: 438px) {
  .adpx_modal-container {
    margin-top: 0px !important;
  }
}

/* Footer text container default */
#adpx_footer-text-container {
  flex-direction: column;
  text-align: right;
  align-items: center;
}

/* Body paragraph */
#adpx_Body > p {
  margin-top: 8px;
}

/* Product description */
#adpx_product-description {
  font-size: 16px;
  font-weight: bold;
  margin-top: 16px;
}

#adpx_product-description > p,
#adpx_product-description > h1,
#adpx_product-description > h2,
#adpx_product-description > h3,
#adpx_product-description > h4,
#adpx_product-description > h5,
#adpx_product-description > span {
  margin: 0;
  font-weight: bold;
}

/* Modal positioning */
.adpx_modal {
  top: 0;
  left: 0;
  z-index: 2147483647;
}

#adpx_popup[data-adpx-position='center'] {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

#adpx_popup[data-adpx-position='bottom-right'] {
  bottom: 0;
  right: 0;
  top: auto;
  left: auto;
  transform: translate(0%, 0%);
}

#adpx_popup[data-adpx-position='bottom-left'] {
  bottom: 0;
  left: 0;
  right: auto;
  top: auto;
  transform: translate(0%, 0%);
}

#adpx_popup[data-adpx-position='top-right'] {
  top: 0;
  right: 0;
  left: auto;
  bottom: auto;
  transform: translate(0%, 0%);
}

#adpx_popup[data-adpx-position='top-left'] {
  top: 0;
  left: 0;
  bottom: auto;
  right: auto;
  transform: translate(0%, 0%);
}

#adpx_popup[data-adpx-position='top-center'] {
  top: 0;
  left: 50%;
  transform: translate(-50%, 0%);
}

#adpx_popup[data-adpx-position='bottom-center'] {
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 0%);
}

#adpx_popup[data-adpx-position='left-center'] {
  top: 50%;
  left: 0;
  transform: translate(0%, -50%);
}

#adpx_popup[data-adpx-position='right-center'] {
  top: 50%;
  right: 0;
  transform: translate(0%, -50%);
}

/* In-modal Perkswall iframe: keep horizontal center, anchor near top (not SOU center transform). */
#adpx_popup:has(#adpx_offerwall_iframe) {
  position: fixed !important;
  top: max(12px, 3vh) !important;
  bottom: auto !important;
  left: 50% !important;
  right: auto !important;
  transform: translateX(-50%) !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

/* Background overlay */
.adpx_modal[data-adpx-background='enabled'] {
  position: fixed;
  height: 100vh;
  width: 100vw;
  background-color: rgba(0, 0, 0, 0.4);
}

/* ============================================================================
   ANIMATIONS
   ============================================================================ */

/* Hold final keyframe after entrance so layout does not snap back before inline styles settle */
#adpx_popup[data-adpx-animate] {
  -webkit-animation-fill-mode: forwards;
  animation-fill-mode: forwards;
}

/* Top to Bottom */
#adpx_popup[data-adpx-animate='top-bottom'] {
  animation: animateTopBottom 0.5s ease;
  -webkit-animation: animateTopBottom 0.5s ease;
}

@keyframes animateTopBottom {
  from {
    transform: translateY(-200%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Bottom to Top */
#adpx_popup[data-adpx-animate='bottom-top'] {
  animation: animateBottomTop 0.5s ease;
  -webkit-animation: animateBottomTop 0.5s ease;
}

@keyframes animateBottomTop {
  from {
    transform: translateY(200%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Left to Right */
#adpx_popup[data-adpx-animate='left-right'] {
  animation: animateLeftRight 0.5s ease;
  -webkit-animation: animateLeftRight 0.5s ease;
}

@keyframes animateLeftRight {
  from {
    transform: translateX(-150%);
    opacity: 0;
  }
  to {
    transform: translateX(0%);
    opacity: 1;
  }
}

/* Right to Left */
#adpx_popup[data-adpx-animate='right-left'] {
  animation: animateRightLeft 0.5s ease;
  -webkit-animation: animateRightLeft 0.5s ease;
}

@keyframes animateRightLeft {
  0% {
    transform: translateX(200%);
    opacity: 0;
  }
  100% {
    transform: translateX(0%);
    opacity: 1;
  }
}

/* Top to Bottom Center */
#adpx_popup[data-adpx-animate='top-bottom-center'] {
  left: 50%;
  animation: animateTopBottomCenter 0.5s ease;
  -webkit-animation: animateTopBottomCenter 0.5s ease;
}

@keyframes animateTopBottomCenter {
  from {
    transform: translate(-50%, -200%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0%);
    opacity: 1;
  }
}

/* Top to Bottom for Center position */
#adpx_popup[data-adpx-animate='top-bottom-forCenter'] {
  left: 50% !important;
  animation: animateTopBottomForCenter 0.5s ease !important;
  -webkit-animation: animateTopBottomForCenter 0.5s ease !important;
}

@keyframes animateTopBottomForCenter {
  from {
    transform: translate(-50%, -200%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0%);
    opacity: 1;
  }
}

/* Bottom to Top Center */
#adpx_popup[data-adpx-animate='bottom-top-center'] {
  animation: animateBottomTopCenter 0.5s ease;
  -webkit-animation: animateBottomTopCenter 0.5s ease;
}

@keyframes animateBottomTopCenter {
  from {
    transform: translate(-50%, 200%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0%);
    opacity: 1;
  }
}

/* Bottom to Top for Center position */
#adpx_popup[data-adpx-animate='bottom-top-forCenter'] {
  animation: animateBottomTopForCenter 0.5s ease;
  -webkit-animation: animateBottomTopForCenter 0.5s ease;
}

@keyframes animateBottomTopForCenter {
  from {
    transform: translate(-50%, 200%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0%);
    opacity: 1;
  }
}

/* Right to Left Center */
#adpx_popup[data-adpx-animate='right-left-center'] {
  animation: animateRightLeftCenter 0.5s ease;
  -webkit-animation: animateRightLeftCenter 0.5s ease;
}

@keyframes animateRightLeftCenter {
  from {
    transform: translateX(150%);
    opacity: 0;
  }
  to {
    transform: translateX(-50%);
    opacity: 1;
  }
}

/* Left to Right Center */
#adpx_popup[data-adpx-animate='left-right-center'] {
  animation: animateLeftRightCenter 0.5s ease;
  -webkit-animation: animateLeftRightCenter 0.5s ease;
}

@keyframes animateLeftRightCenter {
  from {
    transform: translateX(-150%);
    opacity: 0;
  }
  to {
    transform: translateX(-50%);
    opacity: 1;
  }
}

/* Left to Right for Center position */
#adpx_popup[data-adpx-animate='left-right-forCenter'] {
  animation: animateLeftRightForCenter 0.5s ease;
  -webkit-animation: animateLeftRightForCenter 0.5s ease;
}

@keyframes animateLeftRightForCenter {
  from {
    transform: translate(-150%, 0%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0%);
    opacity: 1;
  }
}

/* Right to Left for Center position */
#adpx_popup[data-adpx-animate='right-left-forCenter'] {
  animation: animateRightLeftForCenter 0.5s ease;
  -webkit-animation: animateRightLeftForCenter 0.5s ease;
}

@keyframes animateRightLeftForCenter {
  from {
    transform: translate(200%, 0%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0%);
    opacity: 1;
  }
}

/* ============================================================================
   SHIMMER EFFECT
   ============================================================================ */

.adpx_btn-shimmer {
  position: relative;
  overflow: hidden;
  text-decoration: none;
}

.adpx_btn-shimmer::before {
  content: '';
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--shine-effect);
}

.adpx_btn-shimmer-hover::before {
  left: -100%;
  transition: var(--shine-transition);
}

.adpx_btn-shimmer-hover:hover::before {
  left: 100%;
}

.adpx_btn-shimmer-anim::before {
  animation: shine 3s ease-in-out infinite;
  -webkit-animation: shine 3s ease-in-out infinite;
}

/* Shimmer on SOU CTAs: keep publisher radius; clip ::before to rounded corners */
.adpx_cta-button.adpx_btn-shimmer {
  border-radius: var(--adpx-cta-radius, 4px);
  isolation: isolate;
}

.adpx_cta-button.adpx_btn-shimmer::before {
  border-radius: inherit;
}

@keyframes shine {
  0% {
    left: -100%;
    transition-property: left;
  }
  11.5044247788%,
  100% {
    left: 100%;
    transition-property: left;
  }
}

/* ============================================================================
   LINK STYLES (visited pseudo-class)
   ============================================================================ */

a#adpx_footer-text {
  text-decoration: none;
  color: #777;
}

a#adpx_footer-text:visited {
  text-decoration: none;
  color: #777;
  font-size: 14px;
}

a#adpx_right-cta,
a#adpx_left-cta {
  text-decoration: none;
}

/* Hidden elements */
#adpx_Logo-mobile {
  display: none;
}

img#adpx_PixelTracker {
  display: none;
  width: 1px;
  height: 1px;
}

/* ============================================================================
   V2 GRID LAYOUT
   Shell grid: header / leadin / body / progress / minitext / footer.
   Body grid: text + ctas on one side, image on the other. Falls back to
   a single-column, image-on-top layout when the popup itself is narrow
   (container query), which handles embedded mode correctly.
   ============================================================================ */

.adpx_sou-grid {
  /* Single source of truth for the modal's vertical rhythm.
     Every grid inside the popup reads from this var, so changing it here
     re-spaces the whole modal uniformly. */
  --adpx-gap: 6px;

  container-type: inline-size;
  container-name: adpx-sou;
  display: grid;
  align-content: start;
  width: 100%;
  /* One row per child, in source order (header → lead-in → body → progress →
     mini-text/T&C → footer). We deliberately avoid grid-template-rows/-areas:
     a reserved track for an optional row (lead-in, progress, mini-text) keeps its
     surrounding gap even when empty, leaving a phantom 6px gap. Auto-flow creates
     a track — and a gap — only for children that actually render. */
  grid-auto-rows: auto;
  gap: var(--adpx-gap);
}

#adpx_tnc {
  padding: 0 24px;
}

.adpx_body-grid {
  display: grid;
  padding: 0 24px;
  box-sizing: border-box;
  grid-template-columns: 1fr 160px;
  grid-template-areas:
    'title image'
    'desc  image'
    'ctas  image'
    'perks perks';
  gap: var(--adpx-gap);
  min-height: 116px;
  max-height: 500px;
  height: max-content;
}

.adpx_body-grid[data-image-position='left'] {
  grid-template-columns: 160px 1fr;
  grid-template-areas:
    'image title'
    'image desc'
    'image ctas'
    'perks perks';
}

.adpx_body-grid[data-has-image='false'] {
  grid-template-columns: 1fr;
  grid-template-areas:
    'title'
    'desc'
    'ctas'
    'perks';
}

.adpx_body-grid > .adpx_offer-title       { grid-area: title; min-width: 0; max-width: 100%; margin: 0; }
.adpx_body-grid > .adpx_offer-description { grid-area: desc;  min-width: 0; max-width: 100%; margin: 0; }
.adpx_body-grid > .adpx_cta-area          { grid-area: ctas;  min-width: 0; }
.adpx_body-grid > .adpx_image-area        { grid-area: image; }
.adpx_body-grid > .adpx_perks-area        { grid-area: perks; }

/* Publisher list HTML inside the description (port of legacy styleListElement).
   Left-aligned: indent 20px. Centered: markers inside with no indent so the
   bullets/numbers center with the text. */
.adpx_offer-description ul,
.adpx_offer-description ol {
  padding-left: 20px;
  margin: 0;
}
.adpx_offer-description[data-align='center'] ul,
.adpx_offer-description[data-align='center'] ol {
  list-style-position: inside;
  padding-left: 0;
}

/* Narrow popup (mobile width OR narrow embedded host): stack with image on top.
   Gap keeps reading from --adpx-gap so the rhythm stays consistent. */
@container adpx-sou (max-width: 690px) {
  .adpx_body-grid,
  .adpx_body-grid[data-image-position='left'],
  .adpx_body-grid[data-has-image='false'] {
    display: flex;
    flex-direction: column;
    text-align: center;
  }
  .adpx_body-grid > .adpx_offer-title       { order: 1; }
  .adpx_body-grid > .adpx_image-area        { order: 2; }
  .adpx_body-grid > .adpx_offer-description { order: 3; }
  .adpx_body-grid > .adpx_cta-area          { order: 4; }
  .adpx_body-grid > .adpx_perks-area        { order: 5; }
}

/* Fallback for UAs without container query support (covers shadow DOM edge cases). */
@supports not (container-type: inline-size) {
  @media (max-width: 690px) {
    .adpx_body-grid,
    .adpx_body-grid[data-image-position='left'],
    .adpx_body-grid[data-has-image='false'] {
      display: flex;
      flex-direction: column;
      text-align: center;
    }
    .adpx_body-grid > .adpx_offer-title       { order: 1; }
    .adpx_body-grid > .adpx_image-area        { order: 2; }
    .adpx_body-grid > .adpx_offer-description { order: 3; }
    .adpx_body-grid > .adpx_cta-area          { order: 4; }
    .adpx_body-grid > .adpx_perks-area        { order: 5; }
  }
}

/* ============================================================================
   V2 — PROGRESS + OFFERWALL ROW
   Row on wide popups, stacked column when the popup container narrows.
   Removes the isMobile / flexDirection ternaries from SingleOfferUnit.
   ============================================================================ */

.adpx_sou-progress-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: var(--adpx-gap);
  flex-wrap: wrap;
  padding: 0 24px;
}

.adpx_sou-progress-row > .adpx_sou-progress-col {
  flex: 1 1 auto;
  min-width: 0;
}

.adpx_sou-progress-row > .adpx_sou-offerwall-col {
  flex-shrink: 0;
}

@container adpx-sou (max-width: 690px) {
  .adpx_sou-progress-row {
    flex-direction: column;
    justify-content: stretch;
  }
  .adpx_sou-progress-row > .adpx_sou-progress-col {
    flex: initial;
  }
  .adpx_sou-progress-row > .adpx_sou-offerwall-col {
    text-align: center;
  }
}

@supports not (container-type: inline-size) {
  @media (max-width: 690px) {
    .adpx_sou-progress-row {
      flex-direction: column;
      justify-content: stretch;
    }
    .adpx_sou-progress-row > .adpx_sou-progress-col {
      flex: initial;
    }
    .adpx_sou-progress-row > .adpx_sou-offerwall-col {
      text-align: center;
    }
  }
}

/* ============================================================================
   V2 — OFFERWALL CTA BUTTON
   Publisher colors fed through CSS vars, hover gated on pointer devices.
   No more isMobile / isHovered React state.
   ============================================================================ */

.adpx_offerwall-cta-btn {
  padding: 10px 20px;
  max-height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 24px;
  width: max-content;
  height: max-content;
  margin-bottom: 2px;
  cursor: pointer;
  border: 2px solid var(--adpx-offerwall-border, #007bff);
  border-radius: var(--adpx-offerwall-radius, 4px);
  background-color: var(--adpx-offerwall-bg, #007bff);
  color: var(--adpx-offerwall-color, #ffffff);
  font-size: var(--adpx-offerwall-font-size, 16px);
  font-family: var(--adpx-offerwall-font, Roboto, sans-serif);
  font-weight: var(--adpx-offerwall-font-weight, 400);
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

@media (hover: hover) {
  .adpx_offerwall-cta-btn:hover {
    background-color: var(--adpx-offerwall-hover-bg, #0056b3);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

/* ============================================================================
   V2 — YES / NO CTA BUTTONS
   Stacked vs side-by-side comes from a container query on the grid.
   Hover colors come from CSS vars; JS mouse listeners removed.
   ============================================================================ */

.adpx_cta-container {
  display: flex;
  flex-direction: row;
  gap: var(--adpx-gap);
  align-items: stretch;
  justify-content: flex-start;
  }

@container adpx-sou (max-width: 690px) {
  .adpx_cta-container {
    justify-content: center;
  }
  .adpx_cta-container[data-stacked='true'] {
    flex-direction: column;
  }
}

@supports not (container-type: inline-size) {
  @media (max-width: 690px) {
    .adpx_cta-container {
      justify-content: center;
    }
    .adpx_cta-container[data-stacked='true'] {
      flex-direction: column;
    }
  }
}

.adpx_cta-secondary-row {
  display: flex;
  flex-direction: row;
  gap: var(--adpx-gap);
  width: 100%;
  min-width: 0;
  align-items: stretch;
}

.adpx_cta-secondary-row .adpx_cta-button {
  flex: 1;
  min-width: 0;
}

.adpx_cta-secondary-row .adpx_offerwall-cta-btn {
  flex: 1;
  min-width: 0;
  width: auto;
  height: auto;
  min-height: 44px;
  margin-bottom: 0;
  word-break: break-word;
}

.adpx_cta-container[data-replace-neg='true'] .adpx_offerwall-cta-btn {
  min-width: 0;
  width: auto;
  height: auto;
  max-height: 36px;
  margin-bottom: 0;
  word-break: break-word;
}

.adpx_cta-button {
  padding: 10px 20px;
  max-height: 36px;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  cursor: pointer;
  transition: background-color 0.2s ease;
  text-decoration: none;
  outline: none;
  border-radius: var(--adpx-cta-radius, 4px);
  font-size: var(--adpx-cta-font-size, 15px);
  font-weight: var(--adpx-cta-font-weight, 500);
  font-family: var(--adpx-cta-font, Roboto, sans-serif);
  word-break: break-word;
}

.adpx_cta-button.adpx_positive-cta {
  flex: 7;
  background-color: var(--adpx-pos-bg, #007bff);
  color: var(--adpx-pos-color, #ffffff);
  border: 1px solid var(--adpx-pos-border, transparent);
}

.adpx_cta-button.adpx_negative-cta {
  flex: 3;
  background-color: var(--adpx-neg-bg, transparent);
  color: var(--adpx-neg-color, #007bff);
  border: 1px solid var(--adpx-neg-border, #007bff);
}

@media (hover: hover) {
  .adpx_cta-button.adpx_positive-cta:hover {
    background-color: var(--adpx-pos-hover, var(--adpx-pos-bg, #007bff));
  }
  .adpx_cta-button.adpx_negative-cta:hover {
    background-color: var(--adpx-neg-hover, var(--adpx-neg-bg, transparent));
  }
}

@container adpx-sou (max-width: 690px) {
  .adpx_cta-container {
    align-items: stretch;
  }
  .adpx_cta-button {
    padding: 12px 16px;
    font-size: var(--adpx-cta-font-size, 14px);
    min-width: 0;
    flex: 1;
    max-height: none;
  }
  .adpx_cta-container[data-replace-neg='true'] .adpx_offerwall-cta-btn {
    max-height: none;
  }
}

@supports not (container-type: inline-size) {
  @media (max-width: 690px) {
    .adpx_cta-container {
      align-items: stretch;
    }
    .adpx_cta-button {
      padding: 12px 16px;
      font-size: var(--adpx-cta-font-size, 14px);
      min-width: 0;
      flex: 1;
      max-height: none;
    }
    .adpx_cta-container[data-replace-neg='true'] .adpx_offerwall-cta-btn {
      max-height: none;
    }
  }
}

`;
}
